/**
 * React Query bindings over `./api`.
 *
 * The one rule that matters here: **the acceptance is never cached as a fact.** What is cached
 * is one server answer — «вот что осталось принять» — for half a minute, so that moving between
 * tabs does not fire the same request four times. It is thrown away on every reload, on every
 * acceptance and on every `legal_acceptance_required` from the server, so the client can never
 * end up believing something the server disagrees with. A stored «принято» flag would be both
 * forgeable and unprovable, which is the whole reason this file has no persistence in it.
 *
 * `pendingLegalQueryOptions` is exported as options rather than as a hook because the router's
 * gate reads it in `beforeLoad`, outside React — see `features/legal/gate.ts`.
 */
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/app/query';
import {
  acceptLegalDocument,
  fetchConsentHistory,
  fetchCurrentLegalDocuments,
  fetchLegalDocument,
  fetchPendingLegal,
  type LegalKind,
} from './api';

/**
 * Matches the client-wide default. Long enough that a tab switch does not re-ask, short enough
 * that a redaction published while the app is open is picked up within the same session — and
 * every path that could make it wrong invalidates it explicitly anyway.
 */
export const LEGAL_PENDING_STALE_MS = 30_000;

export function pendingLegalQueryOptions() {
  return queryOptions({
    queryKey: queryKeys.legalPending,
    queryFn: fetchPendingLegal,
    staleTime: LEGAL_PENDING_STALE_MS,
    // A gate that retries twice adds two round trips to a navigation the person is waiting on.
    // The failure path is «пропустить дальше и дать серверу отказать» — see `gate.ts`.
    retry: false,
  });
}

export function legalDocumentQueryOptions(kind: LegalKind) {
  return queryOptions({
    queryKey: queryKeys.legalDocument(kind),
    queryFn: () => fetchLegalDocument(kind),
    // The text of a published redaction does not change under its own version tag, so a longer
    // life here is safe and keeps the gate from re-downloading the offer between two documents.
    staleTime: 5 * 60_000,
  });
}

export function usePendingLegal() {
  return useQuery(pendingLegalQueryOptions());
}

export function useLegalDocument(kind: LegalKind | undefined) {
  return useQuery({
    ...legalDocumentQueryOptions(kind ?? 'offer_and_privacy_policy'),
    enabled: kind !== undefined,
  });
}

/**
 * Records an acceptance and immediately forgets what we thought was pending.
 *
 * `invalidateQueries` rather than writing an optimistic «all accepted» into the cache: the next
 * screen is unblocked by the server's answer, not by ours. With two documents due, the refetch
 * is also what produces the second one.
 */
export function useAcceptLegal() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: acceptLegalDocument,
    onSuccess: () => {
      client.invalidateQueries({ queryKey: queryKeys.legalPending });
      // История только что пополнилась — иначе экран «Мои согласия», открытый в соседней вкладке,
      // показывал бы состояние до подписи.
      client.invalidateQueries({ queryKey: queryKeys.legalHistory });
    },
  });
}

/**
 * The person's own consent log.
 *
 * Not cached beyond the client-wide default and never persisted: this is the same rule the
 * acceptance status follows. A log kept in the browser proves nothing — the copy that matters is
 * the one on the server, and this screen is a window onto it, not a second source of truth.
 */
export function useConsentHistory() {
  return useQuery({
    queryKey: queryKeys.legalHistory,
    queryFn: fetchConsentHistory,
  });
}

/**
 * The current redactions the registration form must echo back.
 *
 * `staleTime: 0` and no retry: this is a snapshot of «что сейчас действует», and a cached one is
 * exactly the thing the server will reject. Cheap enough to re-read on every visit to the form,
 * and a failure is not worth blocking on — see the call site.
 */
export function useCurrentLegalDocuments() {
  return useQuery({
    queryKey: queryKeys.legalCurrent,
    queryFn: fetchCurrentLegalDocuments,
    staleTime: 0,
    retry: false,
  });
}
